// src/hooks/GlobalUserHooks.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchContractorByIdThunk } from '../slices/auth/contractor/thunk';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import type { RootState } from '../index';

export interface UserContextType {
  currentUser: any;
  currentUserId: string;
  userName: string;
  contractor: any;
  contractorId: string | null;
  isContractor: boolean;
  isUserLoading: boolean;
  refreshUser: () => void;
  // Legacy properties for backwards compatibility
  refreshUserContext: () => void;
  userObject: any;
  authUser: any;
  isAuthenticated: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const dispatch: any = useDispatch();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [isUserLoading, setIsUserLoading] = useState<boolean>(true);
  const [hasCheckedContractor, setHasCheckedContractor] = useState<boolean>(false);

  // Get contractor from Redux store
  const contractor = useSelector((state: RootState) => state.Contractor?.contractor);
  const contractorId = contractor?.contractorId || null;
  const isContractor = !!contractor;

  // Helper function to get last contractor ID from Firestore
  const getLastContractorId = async (userId: string): Promise<string | null> => {
    try {
      const firestore = firebase.firestore();
      const docRef = firestore.collection("accountContractorXref").doc(userId);
      const doc = await docRef.get();

      if (!doc.exists) {
        return null;
      }

      const data = doc.data();
      const contractors = data?.contractors || [];

      if (contractors.length === 0) {
        return null;
      }

      // Sort by dateLastLogin to get the most recent
      contractors.sort((a: any, b: any) => {
        const aTime = a.dateLastLogin?.toDate?.() || new Date(a.dateLastLogin);
        const bTime = b.dateLastLogin?.toDate?.() || new Date(b.dateLastLogin);
        return bTime.getTime() - aTime.getTime();
      });

      return contractors[0].contractorId;
    } catch (error) {
      console.error('Error getting last contractor ID:', error);
      return null;
    }
  };

  // Helper function to check URL for cid parameter
  const getContractorIdFromUrl = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('cid');
  };

  // Auto-load contractor when user logs in (if no contractor is already loaded)
  const autoLoadContractor = async (userId: string) => {
    try {
      // Check if there's a cid in the current URL (this handles already logged in users visiting cid URLs)
      const urlContractorId = getContractorIdFromUrl();
      if (urlContractorId) {
        console.log('Found contractor ID in URL, loading:', urlContractorId);
        // Update the accountContractorXref when user visits a cid URL while logged in
        await upsertAccountContractorXref(userId, urlContractorId);
        await dispatch(fetchContractorByIdThunk(urlContractorId));
        setHasCheckedContractor(true);
        return;
      }

      // Don't auto-load if we already have a contractor or if we've already checked
      if (contractor || hasCheckedContractor) {
        return;
      }

      // No cid in URL, check for last contractor association
      const lastContractorId = await getLastContractorId(userId);
      if (lastContractorId) {
        console.log('Auto-loading last contractor for user:', lastContractorId);
        await dispatch(fetchContractorByIdThunk(lastContractorId));
      }

      setHasCheckedContractor(true);
    } catch (error) {
      console.error('Error auto-loading contractor:', error);
      setHasCheckedContractor(true);
    }
  };


  const upsertAccountContractorXref = async (userId: string, contractorId: string) => {
    try {
      const firestore = firebase.firestore();
      const docRef = firestore.collection("accountContractorXref").doc(userId);

      await firestore.runTransaction(async (transaction) => {
        const doc = await transaction.get(docRef);
        const timestamp = firebase.firestore.FieldValue.serverTimestamp();
        const now = new Date(); // CRITICAL: Use Date objects for array items

        if (!doc.exists) {
          const newData = {
            userId,
            contractors: [{
              contractorId,
              dateClaimed: now, // Date object instead of serverTimestamp
              dateLastLogin: now // Date object instead of serverTimestamp
            }],
            dateFirstLogin: timestamp, // serverTimestamp OK at root level
            dateLastLogin: timestamp // serverTimestamp OK at root level
          };
          transaction.set(docRef, newData);
        } else {
          const data = doc.data();
          const contractors = data?.contractors || [];

          const existingContractorIndex = contractors.findIndex(
            (c: any) => c.contractorId === contractorId
          );

          if (existingContractorIndex >= 0) {
            contractors[existingContractorIndex].dateLastLogin = now; // Date object
          } else {
            contractors.push({
              contractorId,
              dateClaimed: now, // Date object
              dateLastLogin: now // Date object
            });
          }

          transaction.update(docRef, {
            contractors,
            dateLastLogin: timestamp // serverTimestamp OK at root level
          });
        }
      });

      console.log('✅ Account contractor xref upserted successfully');
    } catch (error) {
      console.error('❌ Error upserting accountContractorXref:', error);
      throw error;
    }
  };

  const refreshUser = () => {
    const authUser = sessionStorage.getItem('authUser');
    const impersonatedUser = sessionStorage.getItem('impersonatedUser');

    if (impersonatedUser) {
      const user = JSON.parse(impersonatedUser);
      setCurrentUser(user);
      setCurrentUserId(user.uid || user.id || '');
      setUserName(user.displayName || user.firstName || user.email?.split('@')[0] || 'User');
    } else if (authUser) {
      const user = JSON.parse(authUser);
      setCurrentUser(user);
      setCurrentUserId(user.uid || user.id || '');
      setUserName(user.displayName || user.firstName || user.email?.split('@')[0] || 'User');

      // Auto-load contractor if user just logged in
      const userId = user.uid || user.id;
      if (userId && !hasCheckedContractor) {
        autoLoadContractor(userId);
      }
    } else {
      setCurrentUser(null);
      setCurrentUserId('');
      setUserName('');
      setHasCheckedContractor(false);
    }

    setIsUserLoading(false);
  };

  // Initialize user state
  useEffect(() => {
    refreshUser();
  }, []);

  // Listen for storage changes (when user logs in/out or impersonation changes)
  useEffect(() => {
    const handleStorageChange = () => {
      refreshUser();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Reset hasCheckedContractor when user changes
  useEffect(() => {
    if (!currentUserId || currentUserId === '') {
      setHasCheckedContractor(false);
    }
  }, [currentUserId]);

  // Auto-load contractor when user ID becomes available (after login)
  useEffect(() => {
    if (currentUserId && currentUserId !== '' && !hasCheckedContractor) {
      autoLoadContractor(currentUserId);
    }
  }, [currentUserId, hasCheckedContractor, contractor]);

  // Watch for URL changes to handle cid parameter for already logged-in users
  useEffect(() => {
    const handleUrlChange = async () => {
      if (currentUserId && currentUserId !== '') {
        const urlContractorId = getContractorIdFromUrl();
        if (urlContractorId && (!contractor || contractor.contractorId !== urlContractorId)) {
          console.log('URL changed with new contractor ID:', urlContractorId);
          try {
            await upsertAccountContractorXref(currentUserId, urlContractorId);
            await dispatch(fetchContractorByIdThunk(urlContractorId));
          } catch (error) {
            console.error('Error loading contractor from URL change:', error);
          }
        }
      }
    };

    // Initial check
    handleUrlChange();

    // Listen for navigation events (for SPA navigation)
    window.addEventListener('popstate', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [currentUserId, contractor, dispatch]);

  const contextValue: UserContextType = {
    currentUser,
    currentUserId,
    userName,
    contractor,
    contractorId,
    isContractor,
    isUserLoading,
    refreshUser,
    // Legacy properties for backwards compatibility
    refreshUserContext: refreshUser,
    userObject: currentUser,
    authUser: currentUser,
    isAuthenticated: !!currentUser,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const useUserName = (): string => {
  const { userName } = useUser();
  return userName;
};

export const useIsContractor = (): boolean => {
  const { isContractor } = useUser();
  return isContractor;
};

export const useCurrentUserId = (): string => {
  const { currentUserId } = useUser();
  return currentUserId;
};

export const useUserObject = (): any => {
  const { currentUser } = useUser();
  return currentUser;
};

export const withUserContext = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => (
    <UserProvider>
      <Component {...props} />
    </UserProvider>
  );
};